import { formatAutoDispatchFailure } from 'src/components/dispatch/autoDispatch/formatAutoDispatchFailure';
import { AutoDispatchHooks } from 'src/components/dispatch/autoDispatch/useAutoDispatchController';

export type MapAutoDispatchDeps = {
  notify: (message: string, variant?: 'success' | 'error') => void;
  setNotAllocSessionId: (id: string | null) => void;
  /** 実行直前の割当を記録する。mode を問わず毎回撮る（古い差分を残さないため） */
  takeAssignedSnapshot: () => void;
  /** 差分を出すかどうか。自動配車・シナリオ配車の完了時だけ true にする */
  setHighlightEnabled: (enabled: boolean) => void;
  /** スナップショットごと捨てる */
  clearHighlight: () => void;
  /**
   * 自動配車に送った未割当をリストから消す。残ったままだと計算中に「まだ未割当」なのか
   * 「もう割り付いた」のかが読めない（V3Plans hooks/usePlanningRequest.ts:162 と同じ考え方）。
   */
  hideUnassigned: (legRunIds: number[]) => void;
  /** 割り付かなかった案件を戻す。選択は解除していないのでそのまま戻る */
  revealUnassigned: (legRunIds: number[]) => void;
  /** 送った案件を全部戻す。失敗時は 1 件も割り付いていない */
  revealAllUnassigned: () => void;
};

/**
 * `/map` 用の自動配車 8 hooks（設計書「各画面の実装担当」の `/map` 行）。
 *
 * カード配車と違い `/map` は override（ローカルの楽観的更新）を持たないため、
 * override のクリア・復元にまつわる分岐が丸ごと無い。副作用は `notify` /
 * `setNotAllocSessionId` の 2 つだけを注入で受け、hooks 自体は純粋関数として組み立てる
 * （テストで中身を検証できるようにするため）。
 */
export function buildMapAutoDispatchHooks(deps: MapAutoDispatchDeps): AutoDispatchHooks {
  return {
    // /map は override を持たないため run 開始時にすることが無い。型が必須なので渡すが、
    // 意図的な no-op（次の人が「渡し忘れ」と誤解して何か足さないようにこのコメントを残す）
    onRunAccepted: () => {},

    // restrictedInsertPosition（挿入位置の制約）は /map に無いため渡さない

    onStarted: ({ mode, orderIds }) => {
      // 移動・入れ替えのあとに古い差分を残さないため、mode を問わず毎回撮る
      deps.takeAssignedSnapshot();

      // 送った案件は結果が出るまで未割当リストから消す
      deps.hideUnassigned(orderIds);

      if (mode === 'transfer') {
        deps.notify(`${orderIds.length.toLocaleString()}件の配送計画を移動します`);
      } else if (mode === 'swap') {
        // 入れ替えは対象を deliveryId で指定するため件数を出せない。「自動配車を実行中」も
        // 実際の操作と違うので専用の文言にする
        deps.notify('配送計画を入れ替えます');
      } else {
        deps.notify('自動配車を実行中...');
      }
      // 前回の未割当理由 session を捨てる（カード配車の clearNotAllocHint と同じ役割）
      deps.setNotAllocSessionId(null);
    },

    // /map は override を持たないため常に true。controller はこの戻り値を見て
    // clearLocalOverrides を呼ぶかどうかを決めるが、/map に消すべき override が無いので
    // clearLocalOverrides 自体は何もしない（下記）
    shouldClearLocalOverrides: () => true,
    clearLocalOverrides: () => {},

    onCompleted: (result, context) => {
      // 移動・入れ替えは自分で置いた場所なので強調しない。撮るのは全モードだが
      // 有効にするのは自動配車とシナリオ配車だけ
      deps.setHighlightEnabled(context.mode === 'normal' || context.mode === 'scenario');

      // 割り付かなかった案件だけ戻す。割り付いたものは隠したまま（リストからも消える）。
      // サーバの応答で決めるので、未割当一覧の再取得の早い遅いに左右されない
      deps.revealUnassigned(result.notAllocatedOrderIds);

      // カード配車 (ConvenienceDispatches/Presenter.tsx) と同じ条件。`/map` もこれに合わせる。
      // 無条件に保持すると、未割当が 0 件の完了でも直前の session が残ってしまう
      const hasNotAlloc = result.notAllocatedOrderIds.length > 0 && result.sessionId != null;
      deps.setNotAllocSessionId(hasNotAlloc ? result.sessionId : null);

      if (hasNotAlloc) {
        deps.notify(
          `${result.notAllocatedOrderIds.length.toLocaleString()}件が割り当たりませんでした。割当たらない理由を確認してください。`,
        );
      } else {
        deps.notify('自動配車が完了しました', 'success');
      }
    },

    onScopeChanged: () => {
      deps.setNotAllocSessionId(null);
      // 日付・会社が変わると案件の対応関係も変わるため、スナップショットごと捨てる
      deps.clearHighlight();
    },

    /**
     * 利用者が停止したとき。1 件も割り付いていないので、隠した案件を全部戻す。
     * 選択は解除しない（戻ってきた案件をそのまま選び直せるようにする）。
     * onFailed は override の破棄まで巻き添えで走るため cancel からは撃たれない。
     */
    onCancelled: () => {
      deps.setNotAllocSessionId(null);
      deps.revealAllUnassigned();
    },

    onFailed: (error, context) => {
      // scenario の初期応答が非 queued（失敗扱い）のときは onStarted が呼ばれないため、
      // ここで消さないと前回のバッジが残り続ける（設計書 :344-352）
      deps.setNotAllocSessionId(null);
      // 1 件も割り付いていないので、隠した案件を全部戻す
      deps.revealAllUnassigned();
      deps.notify(formatAutoDispatchFailure(error, context), 'error');
    },
  };
}
